import { SelectOption } from 'models/formSingleSelectField';
import { AssetCodeFormatter } from '../../../../components/AssetCodeFormatter';
import Button from '../../../../components/button/Button';
import { useGetAccountCode, useGetAccountDetails } from '../../../../helpers/useGetAccountDetails';
import { AccountFeeInfo, MakePaymentForm, PaymentStepProps } from './MakePayment';
import styles from './MakePayment.module.scss';
import api, { ApiResponse, isAxiosErrorHandled } from '../../../../api';
import { endpoints } from '../../../../endpoints.config';
import { Toast, ToastMessageReason } from '../../../../helpers/toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomerComponentResources } from '../../../../reducers/componentResources';
import { completeUIUpdate } from '../../../../components/notifications/notificationUIUpdateReducer';
import { getFeeLabel } from './AmountPurpose';
import { getRelevantPayloadFields, useIsFinancialInstitution } from '../helpers';
import { useTranslation } from 'react-i18next';
import { MakePaymentMfaModal } from './MakePaymentMfaModal';
import { FiatAccountLink, ProductType } from 'components/sideMenu/SideMenu';
import { FXOrderSummary } from './FXOrderSummary';
import {
    AccountValidationResponse,
    BankAndAccountValidationResponse,
    ErrorDetailValidation,
    FXTransferDetails,
} from './amountPurposeModel';
import { FormikHelpers } from 'formik';
import instance from '../../../../api';
import { getErrorMessage } from 'errors';
import { useTheme } from '@emotion/react';
import { FoldingCube } from 'better-react-spinkit';
import VerifiedBadge from 'components/widgets/VerifiedBadge';
export type FXTranferPaymentForm = MakePaymentForm & {
    quotedFxRate: number;
    quotedFxRateId: number;
    quotedFxRateExpiry: string;
    amountLessFees: number;
    destinationAmountLessFees: number;
    totalFees: number;
};
type Props = PaymentStepProps & {
    data: FXTranferPaymentForm | MakePaymentForm;
    purposes: SelectOption[];
    accountFeeInfo: AccountFeeInfo;
    availableCountries: {
        label: string;
        value: string;
    }[];
    handleEditPayee: () => void;
    payeeId: number | undefined;
    customerAssetAccountsId: number | undefined;
    sourceCurrency: string | undefined;
    destinationCurrency: string | undefined | null;
    bFxTransfer: boolean;
};

const reviewOrder = async (
    values: MakePaymentForm,
    setReviewErrorMessage: React.Dispatch<React.SetStateAction<string>>,
    setFormData: React.Dispatch<React.SetStateAction<MakePaymentForm>>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    bSubmit?: boolean,
    setIsSubmitting?: React.Dispatch<React.SetStateAction<boolean>>
): Promise<any> => {
    const {
        id,
        customerAssetAccountsId,
        destinationAmount,
        amount,
        exchangeType,
        iban,
        accountNumber,
        accountName,
        swiftNumber,
        countryCode,
        currencyCode,
    } = values;
    const payload = {
        payeesId: id,
        customerAssetAccountsId,
        destinationAmount: exchangeType === 'Send' ? undefined : destinationAmount,
        sourceAmount: exchangeType === 'Send' ? amount : undefined,
        transferType: values.transferType,
        accountNumber: accountNumber?.length > 0 ? accountNumber : iban,
        accountName: accountName,
        swift: swiftNumber,
        payeeCountryCode: countryCode,
        currencyCode: currencyCode,
        validatePayee: !bSubmit,
    };
    setLoading(true);
    try {
        const res = await instance.post<ApiResponse<FXTransferDetails>>(
            endpoints.accounts.reviewFxTransfer,
            payload
        );
        if (res.data.status === '1' && res.data.details) {
            const details = res.data.details;
            const update: Partial<FXTranferPaymentForm> = {
                amount: details.commandSourceAmount ?? undefined,
                destinationAmount: details.commandDestinationAmount ?? undefined,
                estimatedAmount: details.estimateSourceAmount,
                estimatedDestinationAmount: details.estimateDestinationAmount,
                quotedFxRate: +details.quotedFXRate,
                quotedFxRateId: details.quotedFXRateID,
                quotedFxRateExpiry: details.quotedFxRateExpiry,
                amountLessFees: details.estimateTotalSourceAmount,
                destinationAmountLessFees: details.estimateTotalDestinationAmount,
                totalFees: details.exchangeFeeAmount,
            };
            setFormData((prev) => ({
                ...prev,
                ...update,
            }));
            return {
                ...values,
                ...update,
                validationStatus: details,
            };
        }
    } catch (err: any) {
        if (
            isAxiosErrorHandled(err) &&
            err.response.data.errors?.some((err) => err.messageCode === 'Insufficient_Funds')
        ) {
            setReviewErrorMessage(getErrorMessage('Insufficient_Funds'));
            setReviewErrorMessage(getErrorMessage('Insufficient_Funds'));
        } else if (
            isAxiosErrorHandled(err) &&
            err.response.data.errors?.some(
                (err) =>
                    err.fieldName === 'TransactionFailed' &&
                    err.messageCode.match(
                        /Deal amount .* is less than transaction minimum size \d*/
                    )
            )
        ) {
            setReviewErrorMessage(getErrorMessage('Amount_Too_Low'));
            setReviewErrorMessage(getErrorMessage('Amount_Too_Low'));
        } else {
            if (!navigator?.onLine) {
                setReviewErrorMessage('Please check your internet connection and try again.');
            } else if (
                err instanceof TypeError &&
                err.message?.includes('Cannot read properties of')
            ) {
                setReviewErrorMessage(
                    'Please contact support (Attempted to access a property on null/undefined).'
                );
            } else {
                setReviewErrorMessage(getErrorMessage('GENERIC'));
            }
        }
        setIsSubmitting?.(false);
    } finally {
        setLoading(false);
    }
    return null;
};

export const transferTypesThatAllowBeneficiaryReference = ['Wire', 'RTP'];

export const ReviewPayment: React.FC<Props> = ({
    data,
    purposes,
    accountFeeInfo,
    nextStep,
    handleEditPayee,
    availableCountries,
    sourceCurrency,
    destinationCurrency,
    bFxTransfer,
}) => {
    const { t } = useTranslation('common');
    const bFinancialInstitution = useIsFinancialInstitution();
    const [loading, setLoading] = useState<boolean>(bFxTransfer);
    const [reviewErrorMessage, setReviewErrorMessage] = useState('');
    const [formData, setFormData] = useState<FXTranferPaymentForm | MakePaymentForm>(data);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const { colors } = useTheme();
    const [validationStatus, setValidationStatus] = useState<
        BankAndAccountValidationResponse | undefined
    >();
    const account = useGetAccountDetails();
    const currencyCode = useGetAccountCode();
    const dispatch = useDispatch();
    const isCardPayee = data.accountType === 'Card';
    const [showEditPayeeButton, setshowEditPayeeButton] = useState(false);
    const [showMfaModal, setShowMfaModal] = useState(false);
    const openMfaModal = () => setShowMfaModal(true);
    const closeMfaModal = () => setShowMfaModal(false);

    const reviewFxOrder = useCallback(async () => {
        setReviewErrorMessage('');
        const res = await reviewOrder(data, setReviewErrorMessage, setFormData, setLoading);
        setshowEditPayeeButton(
            res?.validationStatus?.bankAndAccountValidationResponse?.acountValidation?.status ===
                'not_verified'
        );
        setValidationStatus(res?.validationStatus?.bankAndAccountValidationResponse);
    }, [
        setshowEditPayeeButton,
        setValidationStatus,
        data,
        setFormData,
        setLoading,
        setReviewErrorMessage,
    ]);

    useEffect(() => {
        if (bFxTransfer) {
            reviewFxOrder();
        }
    }, [data, bFxTransfer, reviewFxOrder]);
    const submitPayment = async (tfaCode: string, tfaType: string) => {
        setIsSubmitting(true);
        closeMfaModal();
        setReviewErrorMessage('');
        const payloadAsFormData = new FormData();

        let updatedQuote: FXTranferPaymentForm | undefined;
        if (bFxTransfer) {
            const reviewOrderRes = await reviewOrder(
                formData,
                setReviewErrorMessage,
                setFormData,
                setLoading,
                true,
                setIsSubmitting
            );
            if (reviewOrderRes) {
                payloadAsFormData.append('fxRateId', reviewOrderRes.quotedFxRateId + '');
            }
        }

        const { id, paymentReference, attachments, feeId, beneficiaryReference, ...payload } =
            getRelevantPayloadFields(updatedQuote ? updatedQuote : formData);

        payloadAsFormData.append('customerAssetAccountsId', account?.id + '');
        payloadAsFormData.append('payeesId', id + '');
        payloadAsFormData.append('tfaCode', tfaCode);
        payloadAsFormData.append('tfaType', tfaType);
        feeId && payloadAsFormData.append('bankFeeSplitType', feeId + '');
        payloadAsFormData.append('reference', paymentReference);
        if (
            transferTypesThatAllowBeneficiaryReference.includes(payload.transferType) &&
            beneficiaryReference
        )
            payloadAsFormData.append('beneficiaryReference', beneficiaryReference);
        Object.entries(payload).forEach(([key, value]) => {
            if (value != null) payloadAsFormData.append(key, value + '');
        });
        attachments.forEach((file) => {
            payloadAsFormData.append('attachments', file);
        });

        try {
            const res = await api.post<
                ApiResponse<{
                    bApproved: boolean;
                }>
            >(
                bFxTransfer
                    ? endpoints.accounts.createExternalFXTransfer
                    : endpoints.accounts.createExternalTransfer,
                payloadAsFormData,
                {
                    headers: { 'content-type': 'multipart/form-data' },
                }
            );

            if (res.data.status === '1') {
                if (!res.data.details.bApproved) {
                    Toast.openToastMessage(
                        "Your transaction has been flagged for review. The funds have been reserved and we'll let you know when it has been approved",
                        ToastMessageReason.ERROR,
                        { autoClose: false }
                    );
                    dispatch(getCustomerComponentResources());
                    dispatch(completeUIUpdate());
                } else {
                    Toast.openSuccessToast('Transfer succeeded');
                    dispatch(getCustomerComponentResources());
                    dispatch(completeUIUpdate());
                }
                nextStep();
            }
        } catch (err: any) {
            if (isAxiosErrorHandled(err)) {
                const errors = err.response.data.errors;
                if (
                    errors.some(
                        (err) => err.fieldName === 'PayeesId' && err.messageCode === 'Invalid'
                    )
                ) {
                    Toast.openToastMessage('Invalid Payee', ToastMessageReason.ERROR);
                } else if (
                    errors.some((err) => err.fieldName.toLowerCase().includes('transmitter'))
                ) {
                    setshowEditPayeeButton(true);
                    Toast.openErrorToast('Missing transmitting info');
                } else if (errors.some((err) => err.fieldName === 'tfaCode')) {
                    Toast.openErrorToast('Invalid TFA code');
                } else Toast.parseErrorIntoToast(err.response.data.errors?.[0]?.messageCode);
            } else {
                if (!navigator?.onLine) {
                    setReviewErrorMessage('Please check your internet connection and try again.');
                    Toast.openErrorToast('No internet connection!');
                } else if (
                    err instanceof TypeError &&
                    err.message?.includes('Cannot read properties of')
                ) {
                    setReviewErrorMessage(
                        'Please contact support (Attempted to access a property on null/undefined).'
                    );
                } else if (err?.code === 'ECONNABORTED') {
                    setReviewErrorMessage('Request timed out!');
                    Toast.openErrorToast('Request timed out!');
                } else if (err?.code === 'ERR_NETWORK') {
                    setReviewErrorMessage('Please check your internet connection and try again.');
                    Toast.openErrorToast('No internet connection!');
                } else {
                    Toast.openGenericErrorToast();
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const purpose = purposes.find((item) => item.value === formData.purpose);
    const country =
        availableCountries.find((item) => item.value === formData.countryCode)?.label ??
        formData.countryCode;
    const payeeCountry =
        availableCountries.find((item) => item.value === formData.addressCountryCode)?.label ??
        formData.addressCountryCode;

    const feeOptions =
        accountFeeInfo.transferTypes.find(
            (fee) => fee.transferType.toLowerCase() === formData.transferType.toLowerCase()
        )?.feeOptions ?? [];
    const fee = feeOptions.find((fee) => fee.bankFeeSplitType === formData.feeId);

    const exactPrice = bFxTransfer ? (formData.exchangeType === 'Send' ? 'Left' : 'Right') : null;

    return (
        <div className={styles.ReviewPayment}>
            {loading ? (
                <div className={styles.Loading}>
                    <FoldingCube color={colors.first} size={80} />
                </div>
            ) : (
                <>
                    <MakePaymentMfaModal
                        open={showMfaModal}
                        handleClose={closeMfaModal}
                        handleSubmit={submitPayment}
                    />
                    {bFxTransfer && (
                        <FXOrderSummary
                            reviewErrorMessage={reviewErrorMessage}
                            transferDetails={formData as FXTranferPaymentForm}
                            sourceCurrency={sourceCurrency as string}
                            destinationCurrency={destinationCurrency as string}
                            exactPrice={exactPrice as 'Left' | 'Right'}
                            refreshReview={reviewFxOrder}
                        />
                    )}
                    <div className={styles.Layout}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <h4>Recipient</h4>
                                {validationStatus?.acountValidation?.status === 'not_supported' && (
                                    <VerifiedBadge
                                        title="Unable to verify Account"
                                        type="WARNING"
                                    />
                                )}
                                {validationStatus?.acountValidation?.status === 'verified' && (
                                    <VerifiedBadge title="Verified" type="VERIFIED" />
                                )}
                            </div>
                            <dl className={styles.Recipient}>
                                {!isCardPayee && (
                                    <div className={styles.Row}>
                                        <dt>Account Type:</dt>
                                        <dd>{formData.payeeType || '-'}</dd>
                                    </div>
                                )}
                                <div className={styles.Row}>
                                    <dt>Account Country:</dt>
                                    <dd>{country || '-'}</dd>
                                </div>
                                {!isCardPayee && (
                                    <div className={styles.Row}>
                                        <dt>Account Name:</dt>
                                        <dd>{formData.accountName || '-'}</dd>
                                    </div>
                                )}
                                <div className={styles.Row}>
                                    <dt>Payee Name:</dt>
                                    <dd>{formData.name}</dd>
                                </div>
                                {!isCardPayee && (
                                    <>
                                        <div className={styles.Row}>
                                            <dt>Payee Country:</dt>
                                            <dd>{payeeCountry || '-'}</dd>
                                        </div>
                                        <div className={styles.Row}>
                                            <dt>Payee Reference:</dt>
                                            <dd>{formData.payeesReference || '-'}</dd>
                                        </div>
                                    </>
                                )}
                                {formData.swiftNumber && (
                                    <div className={styles.Row}>
                                        <dt>Swift Number:</dt>
                                        <dd>{formData.swiftNumber}</dd>
                                    </div>
                                )}
                                {formData.intermediaryBic && (
                                    <div className={styles.Row}>
                                        <dt>Intermediary Swift:</dt>
                                        <dd>{formData.intermediaryBic}</dd>
                                    </div>
                                )}
                                {formData.iban && (
                                    <div className={styles.Row}>
                                        <dt>IBAN:</dt>
                                        <dd>{formData.iban}</dd>
                                    </div>
                                )}
                                {formData.routingNumber && (
                                    <div className={styles.Row}>
                                        <dt>Routing Number:</dt>
                                        <dd>{formData.routingNumber}</dd>
                                    </div>
                                )}
                                {formData.accountNumber && (
                                    <div className={styles.Row}>
                                        <dt>Account Number:</dt>
                                        <dd>{formData.accountNumber}</dd>
                                    </div>
                                )}
                            </dl>
                            {isCardPayee && (
                                <>
                                    <h4>Payment details</h4>
                                    <dl>
                                        <div className={styles.Row}>
                                            <dt>Payment Method:</dt>
                                            <dd><span className="notranslate" translate="no">{data.transferType}</span></dd>
                                        </div>
                                        <div className={styles.Row}>
                                            <dt>Payment Reference:</dt>
                                            <dd>
                                                <span className="notranslate" translate="no">
                                                    {data.paymentReference || '-'}
                                                </span>
                                            </dd>
                                        </div>
                                        {(account as FiatAccountLink)?.productDisplayName ===
                                            ProductType.CRB && (
                                            <div className={styles.Row}>
                                                <dt>Beneficiary Reference:</dt>
                                                <dd>
                                                    <span className="notranslate" translate="no">
                                                        {data.beneficiaryReference || '-'}
                                                    </span>
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </>
                            )}
                        </div>
                        <div>
                            {!isCardPayee && (
                                <>
                                    <h4>Payment details</h4>
                                    <dl>
                                        <div className={styles.Row}>
                                            <dt>Payment Method:</dt>
                                            <dd><span className="notranslate" translate="no">{formData.transferType}</span></dd>
                                        </div>
                                        <div className={styles.Row}>
                                            <dt>Payment Reference:</dt>
                                            <dd>
                                                <span className="notranslate" translate="no">
                                                    {formData.paymentReference || '-'}
                                                </span>
                                            </dd>
                                        </div>
                                        {(account as FiatAccountLink)?.productDisplayName ===
                                            ProductType.CRB && (
                                            <div className={styles.Row}>
                                                <dt>Beneficiary Reference:</dt>
                                                <dd>
                                                    <span className="notranslate" translate="no">
                                                        {formData.beneficiaryReference || '-'}
                                                    </span>
                                                </dd>
                                            </div>
                                        )}
                                    </dl>
                                </>
                            )}

                            <h4>Amount & purpose</h4>
                            <dl>
                                {fee && feeOptions.length > 1 && (
                                    <div className={styles.Row}>
                                        <dt>Fee:</dt>
                                        {/* @ts-ignore */}
                                        <dd>{getFeeLabel(fee.bankFeeSplitType).label}</dd>
                                    </div>
                                )}
                                <div className={styles.Row}>
                                    {exactPrice ? (
                                        <>
                                            <dt>{`${
                                                exactPrice === 'Left' ? 'Exact ' : 'Estimated '
                                            }Amount (including fees):`}</dt>
                                            <dd>
                                                {exactPrice === 'Left'
                                                    ? formData.amount
                                                    : formData.estimatedAmount}{' '}
                                                <AssetCodeFormatter
                                                    assetCode={sourceCurrency as string}
                                                />
                                            </dd>
                                        </>
                                    ) : (
                                        <>
                                            <dt>Amount:</dt>
                                            <dd>
                                                {formData.amount}{' '}
                                                <AssetCodeFormatter assetCode={currencyCode} />
                                            </dd>
                                        </>
                                    )}
                                </div>
                                {bFxTransfer && !reviewErrorMessage && (
                                    <>
                                        <div className={styles.Row}>
                                            <dt>Total Exchange Fees:</dt>
                                            <dd>
                                                {(formData as FXTranferPaymentForm).totalFees}{' '}
                                                <AssetCodeFormatter
                                                    assetCode={sourceCurrency as string}
                                                />
                                            </dd>
                                        </div>
                                        <div className={styles.Row}>
                                            <dt>{`${
                                                exactPrice === 'Right' ? 'Exact ' : 'Estimated '
                                            }Recipient Amount:`}</dt>
                                            <dd>
                                                {
                                                    (formData as FXTranferPaymentForm)
                                                        .destinationAmountLessFees
                                                }{' '}
                                                <AssetCodeFormatter
                                                    assetCode={destinationCurrency as string}
                                                />
                                            </dd>
                                        </div>
                                    </>
                                )}
                                <div className={styles.Row}>
                                    <dt><span className="notranslate" translate="no">{formData.transferType}</span>{' '}Payment Purpose:</dt>
                                    {/* @ts-ignore */}
                                    <dd>{purpose ? purpose.label : formData.purpose || '-'}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <h4>More details</h4>
                    <dl>
                        <div className={styles.Row}>
                            <dt>Attachments:</dt>
                            <dd>
                                {formData.attachments.length > 0
                                    ? formData.attachments.reduce(
                                          (prev, cur, i) =>
                                              prev + `${i !== 0 ? ', ' : ''}${cur.name}`,
                                          ''
                                      )
                                    : '-'}
                            </dd>
                        </div>
                        <div className={styles.Row}>
                            <dt>Notes:</dt>
                            <dd>{formData.notes || '-'}</dd>
                        </div>
                    </dl>
                    {validationStatus?.acountValidation?.status === 'not_verified' && (
                        <>
                            <h4>Verification details</h4>
                            <dl>
                                <div className={styles.Row}>
                                    <dt>Not Verified:</dt>
                                    <dd>
                                        {validationStatus?.acountValidation?.beneficiary
                                            ?.bank_account_name ??
                                            validationStatus?.acountValidation?.errors
                                                ?.map((error) => error.message)
                                                .join(', ') ??
                                            ''}
                                    </dd>
                                </div>
                            </dl>
                        </>
                    )}
                    {showEditPayeeButton && (
                        <Button
                            className="MarginBottom"
                            type="button"
                            priority="secondary"
                            disabled={isSubmitting}
                            onClick={handleEditPayee}
                            color="first"
                        >
                            Edit Payee
                        </Button>
                    )}
                    <Button
                        disabled={
                            (bFxTransfer && !!reviewErrorMessage) ||
                            validationStatus?.acountValidation?.status === 'not_verified' ||
                            isSubmitting ||
                            loading
                        }
                        onClick={openMfaModal}
                        color="third"
                        showSubmittingSpinner={isSubmitting}
                    >
                        {t('confirmPayment')}
                    </Button>
                </>
            )}
        </div>
    );
};
